import {
  Box,
  Button,
  ButtonIcon,
  Footer,
  GridedContent,
  GridedLayout,
  GridedMain,
  Header,
  IconCheckCircle,
  IconClear,
  Spacer,
  Typography,
  useLayout,
} from '@capitalise/ui-design-system';
import { useModalContext } from 'hooks/useModalContext';
import { useEffect } from 'react';
import { useQueryGetCompanies } from 'services/auth/queries';
import { useCountryContext } from 'hooks/useCountryContext';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDebouncedCallback } from 'use-debounce';
import styled from 'styled-components';
import { useDownload } from 'hooks/useDownload';
import { dateFormat } from 'utils/DateHelper';
import { getCapitalReportPdfByCompanyNumber } from 'services/monitor';
import { postNewEntity } from 'services/monitor/entity';
import DownloadScreen from './DownloadScreen';
import ErrorScreen from './ErrorSceen';
import { useDownloadPdf } from './hooks/useDownloadPdf';
import { UserCompanyType } from 'modules/Business/shared/typesTS';
import { EntityTypeEnum } from 'types/entity';


interface IDownloadPdfProps {
  userName: string;
  userEmail: string;
  getClients: Function;
  entityId: number;
}

export interface ISelectedCompany {
  number: number;
  label: string;
  type: string;
}

const schema = yup.object().shape({
  name: yup
    .string()
    .min(3, 'Please enter your company’s name or number')
    .required('Please enter your company’s name or number'),
  addCompany: yup.boolean().default(false),
});

const NoPaddingFooter = styled(Footer)`
  padding: 0;
`;

const DownloadPdf = ({ userName, userEmail, entityId, getClients }: IDownloadPdfProps) => {
  const { close } = useModalContext();
  const { renderToaster } = useLayout();

  const {
    selectedCompany,
    setSelectedCompany,
    isOpen,
    setIsOpen,
    downloadError,
    toggleDownloadError,
    query,
    setQuery,
  } = useDownloadPdf();

  const { country } = useCountryContext();
  const { loadingDownload, handleDownload } = useDownload();

  const createPayloadForBusiness = (company) => ({
    name: company.label,
    registrationNumber: company.number,
    type: EntityTypeEnum.BUSINESS,
    business: {
      type:
        company.type && company.type === 'NonLtd'
          ? UserCompanyType.soleTrader
          : UserCompanyType.limitedCompany,
      contact: {
        name: userName,
        email: userEmail,
      },
    },
    countryCode: country,
  });

  const { getValues, setValue, handleSubmit } = useForm({
    mode: 'all',
    resolver: yupResolver(schema),
  });

  const {
    refetch,
    data: companies,
    isRefetching: isLoading,
  } = useQueryGetCompanies(query, country);

  const searchHandler = useDebouncedCallback(() => {
    if (query.length > 2) {
      refetch();
      return;
    }
  }, 300);

  useEffect(searchHandler, [query, country]);

  const onSubmit = async () => {
    if (downloadError) {
      close();
      return;
    }

    const payload = getValues('addCompany') ? createPayloadForBusiness(selectedCompany) : {};
    const companyNumber = selectedCompany.number;

    const fileName = `${selectedCompany.label
      .replaceAll(' ', '_')
      .replaceAll(/[^\w\s]/gi, '')}-capital_report-${dateFormat(new Date(), 'yyyy-MM-dd')}.pdf`;

    const addCompanyPromise = getValues('addCompany') ? [postNewEntity(payload)] : [];

    try {
      const [downloadResult] = await Promise.allSettled([
        handleDownload(
          fileName,
          'Unable to generate report',
          async () => await getCapitalReportPdfByCompanyNumber(companyNumber),
        ),
        ...addCompanyPromise,
      ]);

      if (downloadResult.status === 'rejected') {
        throw {};
      }

      getValues('addCompany') && getClients?.(entityId);

      renderToaster(
        <Spacer gap="tiny" alignItems="center">
          <IconCheckCircle color="neutral-white" />
          <Typography size="body-14" color="white">
            Your report has downloaded!
          </Typography>
        </Spacer>,
      );
      close();
    } catch (error) {
      toggleDownloadError();
    }
  };

  return (
    <>
      <Box backgroundColor="white">
        <form onSubmit={handleSubmit(onSubmit)}>
          <GridedLayout>
            <Header
              actions={
                <ButtonIcon active baseColor="grey" onClick={close} round size="medium">
                  <IconClear />
                </ButtonIcon>
              }
              isPoweredByExperian
            />
            <GridedMain>
              <GridedContent position="relative">
                <Box
                  gridColumn={{ _: '1/5', sm: '3/7', lg: '5/9' }}
                  display="flex"
                  flexDirection="column"
                  py="medium"
                >
                  {downloadError ? (
                    <ErrorScreen />
                  ) : (
                    <DownloadScreen
                      selectedCompany={selectedCompany}
                      setSelectedCompany={setSelectedCompany}
                      isLoading={isLoading}
                      isOpen={isOpen}
                      setIsOpen={setIsOpen}
                      companies={companies}
                      setQuery={setQuery}
                      setValue={setValue}
                    />
                  )}
                </Box>
              </GridedContent>
            </GridedMain>
            <NoPaddingFooter>
              <GridedContent backgroundColor="white" width="100%">
                <Box width="100%" margin="auto" gridColumn={{ _: '1/5', sm: '3/7', lg: '5/9' }}>
                  <Button
                    baseColor="green"
                    size="large"
                    fullWidth
                    type="submit"
                    loading={loadingDownload}
                    disabled={!getValues('name')}
                  >
                    {downloadError ? 'Continue' : 'Download report'}
                  </Button>
                </Box>
              </GridedContent>
            </NoPaddingFooter>
          </GridedLayout>
        </form>
      </Box>
    </>
  );
};

export default DownloadPdf;
